/**
 * Axios Configuration with CORS & environment handling
 * 
 * This centralizes all API communication configuration
 * Ensures proper headers, credentials, and error handling across all environments
 */

import axios from 'axios'
import config from '@/config/index.js'

// Get API base URL from environment
const apiBaseUrl = config.apiBaseUrl

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: apiBaseUrl,
  timeout: config.getSetting('apiTimeout'),
  withCredentials: true, // Send cookies with requests
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Request Interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add CSRF token for state-changing methods
    const methods = ['post', 'put', 'patch', 'delete']
    if (methods.includes(config.method?.toLowerCase())) {
      const csrfToken = getCookie('csrftoken')
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken
      }
    }

    // Log request in development
    if (config.isDevelopment && config.getSetting('enableDetailedErrors')) {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`)
    }

    return config
  },
  (error) => {
    console.error('[API Request Error]', error)
    return Promise.reject(error)
  }
)

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    if (config.isDevelopment && config.getSetting('enableDetailedErrors')) {
      console.log(`[API] ✓ ${response.status} ${response.config.url}`)
    }
    return response
  },
  (error) => {
    // Handle CORS errors
    if (error.message === 'Network Error' && !error.response) {
      console.error('[CORS/Network Error]', {
        message: 'Failed to connect to API',
        apiBaseUrl: apiBaseUrl,
        environment: config.environment,
        suggestion: 'Check CORS configuration and API URL',
      })
      
      // More specific error message based on environment
      if (config.isDevelopment) {
        console.error('Development Checklist:')
        console.error('1. Backend running at:', apiBaseUrl)
        console.error('2. CORS_ALLOWED_ORIGINS includes:', window.location.origin)
        console.error('3. Check .env: VITE_API_BASE_URL =', apiBaseUrl)
      }
    }

    // Log detailed errors in development
    if (config.isDevelopment && config.getSetting('enableDetailedErrors')) {
      console.error('[API Response Error]', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        data: error.response?.data,
      })
    }

    return Promise.reject(error)
  }
)

/**
 * Get cookie value by name
 * Used for CSRF token extraction
 */
function getCookie(name) {
  let cookieValue = null
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';')
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim()
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1))
        break
      }
    }
  }
  return cookieValue
}

/**
 * Log API configuration (for debugging)
 */
export function logApiConfig() {
  console.group('[API Configuration]')
  console.log('Base URL:', apiBaseUrl)
  console.log('Environment:', config.environment)
  console.log('With Credentials:', apiClient.defaults.withCredentials)
  console.log('Timeout:', config.getSetting('apiTimeout') + 'ms')
  console.log('Frontend Origin:', window.location.origin)
  console.groupEnd()
}

export default apiClient
